import React from "react";
// 1. Import Helmet
import { Helmet } from "react-helmet";
import Header from "../Components/Header";
import Footer from "../Components/Footer";

// Define metadata constants for SEO
const PAGE_TITLE = "Transparent Pricing for Vessel & Cargo Search | Steminorder";
const PAGE_DESCRIPTION = "Choose the best plan for your maritime business. Steminorder offers simple, transparent pricing for access to global cargo, vessel, and company data.";
const CANONICAL_URL = "https://www.steminorder.com/pricing";
const OG_IMAGE_URL = "https://www.steminorder.com/images/pricing-hero.png"; // Use a relevant image

const PricingPage = () => {
  return (
    <>
      {/* ============================================================
        REACT HELMET INTEGRATION 
        ============================================================
      */}
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />

        {/* Canonical URL */}
        <link rel="canonical" href={CANONICAL_URL} />

        {/* Open Graph Tags */}
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:url" content={CANONICAL_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Steminorder" />
        <meta property="og:image" content={OG_IMAGE_URL} /> 

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE_URL} />
      </Helmet>
      
      <Header />

      {/* Inline component-scoped styles to ensure list icons align with text */}
      <style>{`
        .list-item-with-icon-blue { display: flex; align-items: flex-start; gap: 8px; }
        .list-item-with-icon-blue i { margin-top: 4px; /* small offset so icon visually lines up with text start */ }
        .text-link { color: inherit; }
      `}</style>

      <div className="container">
        <div className="row">
          <div className="col">
            <h4 className="text-center text- text-base pt-10 pb-4 text-breadcrumb font-bold"> PRICING </h4>
          </div>
        </div>
        <div className="row">
          <div className="col-8 offset-2">
            <h1 className="text-center text-5xl font-bold py-3 text-heading"> Simple, transparent pricing </h1>
            <p className="text-center leading-loose text-paragraph"> Choose the plan that fits your maritime business — clear features and reliable email delivery via AWS SES. </p>
          </div>
        </div>


        <div className="row mt-2">
          {/* CARD 1 - Basic (FREE) */}
          <div className="col-lg-4 col-sm-12 mt-5">
            <div className="card rounded-4">
              <div className="card-body rounded-4 p-5">
            
                <h6 className="text-heading text-base font-bold pt-0 pb-3"> Basic — FREE </h6>
                <p className="text-base text-paragraph"> Access core features to start searching and matching cargoes, vessels and company contacts. </p>

                <p className="font-semibold text-heading pt-3"> What’s included </p>
                <ul className="lh-lg px-0 pb-3">
                  <li className="list-item-with-icon-blue mb-3"><i className="fa fa-check text-primary" aria-hidden="true"></i> <span>Cargo &amp; Vessel Position Entry &amp; Search</span></li>
                  <li className="list-item-with-icon-blue mb-3"><i className="fa fa-check text-primary" aria-hidden="true"></i> <span>AI-Supported Email Entry and Matching</span></li>
                  <li className="list-item-with-icon-blue mb-3"><i className="fa fa-check text-primary" aria-hidden="true"></i> 
                <span>  Online   <a href="/services#legal" className="text-link" style={{color: '#0d6efd', textDecoration: 'underline'}}> Legal Assistance </a> by STEMINORDER Lawyers for initial consultations</span></li>
                  <li className="list-item-with-icon-blue mb-3"><i className="fa fa-check text-primary" aria-hidden="true"></i> <span>Unlimited 50x50 Email Campaigns to All Companies for All Cargoes &amp; Vessels</span></li>
                  <li className="list-item-with-icon-blue mb-3"><i className="fa fa-check text-primary" aria-hidden="true"></i> <span>Live Trade Announcements</span></li>
                  <li className="list-item-with-icon-blue mb-3"><i className="fa fa-check text-primary" aria-hidden="true"></i> <span>Human Resources Entry &amp; Search</span></li>
                  <li className="list-item-with-icon-blue mb-3"><i className="fa fa-check text-primary" aria-hidden="true"></i> <span>5,000 Email Sends to Your Own Mailing List via Your Dedicated AWS  <a href="/services#circulation" className="text-link" style={{color: '#0d6efd', textDecoration: 'underline'}}> Circulation Platform </a></span></li>
                  <li className="list-item-with-icon-blue mb-3"><i className="fa fa-check text-primary" aria-hidden="true"></i> <span>STEMINORDER Reference Card Entry &amp; Search for Companies &amp; Vessels</span></li>
                </ul>

                <div className="d-flex justify-center">
<button
  className="btn btn-primary px-5 py-3 rounded-5 font-bold w-100"
  onClick={() => window.location.href = "https://portal.steminorder.com/sign-in"}
>
  Get Started
</button>

                </div>
              </div>
            </div>
          </div>

          {/* CARD 2 - Business Class */}
          <div className="col-lg-4 col-sm-12">
            <div className="card rounded-4">
              <div className="card-body bg-header-green text-white rounded-4 p-5">
          
                <h6 className=" text-base font-bold pt-0 pb-3"> Business Class — $390 / Year </h6>
                <p className="text-base "> In addition to all Basic features: </p>

                <ul className="lh-lg px-0 pb-3">
                  <li className="list-item-with-icon-blue mb-3"><i className="fa fa-check text-primary" aria-hidden="true"></i> <span>20% Discount on <a href="/services#legal" className="text-link" style={{color: '#0d6efd', textDecoration: 'underline'}}>  Legal Assistance  </a>by STEMINORDER Lawyers</span></li>
                  <li className="list-item-with-icon-blue mb-3"><i className="fa fa-check text-primary" aria-hidden="true"></i> <span>Access Full Details of All Members and Vessels</span></li>
                  <li className="list-item-with-icon-blue mb-3"><i className="fa fa-check text-primary" aria-hidden="true"></i> <span>One Mass Mailing per Day to All Members from your mail address via <strong>offer@steminorder.com</strong></span></li>
                  <li className="list-item-with-icon-blue mb-3"><i className="fa fa-check text-primary" aria-hidden="true"></i> <span>100,000 Email Sends to Your Own Mailing List via Your Dedicated AWS <a href="/services#circulation" className="text-link" style={{color: '#0d6efd', textDecoration: 'underline'}}> Circulation Platform </a></span></li>
                </ul>

                <div className="d-flex justify-center">
<button
  className="btn btn-primary px-5 py-3 rounded-5 font-bold w-100"
  onClick={() => window.location.href = "https://portal.steminorder.com/sign-in"}
>
  Get Started
</button>

                </div>
              </div>
            </div>
          </div>

          {/* CARD 3 - Premium */}
          <div className="col-lg-4 col-sm-12 mt-5">
            <div className="card rounded-4">
              <div className="card-body rounded-4 p-5">
        
                <h6 className="text-heading text-base font-bold pt-0 pb-3"> Premium — $990 / Year </h6>
                <p className="text-base text-paragraph"> In addition to all Business Class features: </p>

                <ul className="lh-lg px-0 pb-3">
                  <li className="list-item-with-icon-blue mb-3"><i className="fa fa-check text-primary" aria-hidden="true"></i> <span>Comprehensive AI Support Across All Operations</span></li>
                  <li className="list-item-with-icon-blue mb-3"><i className="fa fa-check text-primary" aria-hidden="true"></i> <span>Customer-Based Machine Learning &amp; Recommendation Engine</span></li>
                  <li className="list-item-with-icon-blue mb-3"><i className="fa fa-check text-primary" aria-hidden="true"></i> <span>Digital Documentation Secured by Blockchain</span></li>
                  <li className="list-item-with-icon-blue mb-3"><i className="fa fa-check text-primary" aria-hidden="true"></i> <span>Coming Soon: Distance Table &amp; Voyage Estimation Tool</span></li>
                  <li className="list-item-with-icon-blue mb-3"><i className="fa fa-check text-primary" aria-hidden="true"></i> <span>1,000,000 Email Sends to Your Own Mailing List via Your Dedicated AWS <a href="/services#circulation" className="text-link" style={{color: '#0d6efd', textDecoration: 'underline'}}> Circulation Platform </a></span></li>
                </ul>

                <div className="d-flex justify-center">
<button
  className="btn btn-primary px-5 py-3 rounded-5 font-bold w-100"
  onClick={() => window.location.href = "https://portal.steminorder.com/sign-in"}
>
  Get Started
</button>

                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row mt-4">
          <div className="col-12 text-center">
            <p className="small">All email services are powered by <strong>AWS SES (Amazon)</strong> for maximum reliability and security.</p>
          </div>
        </div>

      </div>
      <Footer />
    </>
  );
};

export default PricingPage;
